import { uniq } from "lodash-es";
import { DependencyList } from "react";

import { SvgPointStyle } from "@/common/ts/svg";
import { VisualModelImageSelector, VisualModelPoint } from "@/common/ts/visualModel";
import { apiClient } from "@/core/api/ApiClient";
import {
  VehicleDamageDataDto,
  VehicleDamageDto,
  VehicleDamageState,
  VehicleSpecDto,
  VehicleSpecInputDto,
  VehicleVisualModelDto,
  VehicleVisualModelImageDto,
  VehicleVisualModelSvgPathMetadataDto,
} from "@/core/api/generated/v1";

import { ArrayHelper } from "../array";
import { SvgHelper } from "../svg";
import { TypeHelper } from "../type";

export class VehicleVisualModelHelper {
  /** Returns the most suitable or default VehicleVisualModel for provided VehicleSpec. */
  public static async getSuggestedModel(
    spec: VehicleSpecDto | VehicleSpecInputDto | null | undefined,
  ): Promise<VehicleVisualModelDto | null> {
    const spec2 = spec as VehicleSpecDto | undefined;
    const specInput = spec as VehicleSpecInputDto | undefined;

    const response =
      await apiClient.vehicleVisualModelsApi.apiV1ReferenceDataVehiclesVisualModelsLatestOrDefaultGet(
        {
          vehicleType: spec?.type || undefined,
          bodyType: spec?.bodyType || undefined,
          makeId: spec2?.make?.id || specInput?.makeId || undefined,
          modelId: spec2?.model?.id || specInput?.modelId || undefined,
          generationId: spec2?.generation?.id || specInput?.generationId || undefined,
          modificationId: spec2?.modification?.id || specInput?.modificationId || undefined,
        },
      );
    return response.data;
  }

  public static getFirstImage(
    visualModel: Nil<VehicleVisualModelDto>,
  ): Nil<VehicleVisualModelImageDto> {
    return visualModel?.images?.at(0);
  }

  /** Flattens select and ensures it can be properly used as hood deps. */
  public static getImageSelectorDeps(selector: Nil<VisualModelImageSelector>): DependencyList {
    return [selector?.imageId, selector?.image, selector?.area, selector?.projection];
  }

  public static getImageSelectorsDeps(
    selectors: Nil<Nil<VisualModelImageSelector>[]>,
  ): DependencyList {
    return selectors?.flatMap((selector) => this.getImageSelectorDeps(selector)) || [];
  }

  public static getImageBySelector(
    visualModel: Nil<VehicleVisualModelDto>,
    selector: Nil<VisualModelImageSelector>,
    points?: Nil<VisualModelPoint[]>,
  ): Nil<VehicleVisualModelImageDto> {
    if (!visualModel || !selector) {
      return null;
    }

    const specificImage = visualModel?.images?.find((image) =>
      selector.imageId
        ? image.id === selector
        : selector.image
          ? image.id === selector.image.id
          : false,
    );
    if (specificImage) {
      return specificImage;
    }

    const firstImageWithPoints = selector.isFirstWithPoints
      ? visualModel?.images?.find((image) => {
          const hasPoints =
            points?.some(
              (point) =>
                point.meta.area === image.area && point.meta.projection === image.projection,
            ) ?? false;
          return hasPoints;
        })
      : null;
    if (firstImageWithPoints) {
      return firstImageWithPoints;
    }

    const otherImage = visualModel?.images?.find((image) =>
      selector.area && selector.projection
        ? image.area == selector.area && image.projection === selector.projection
        : false,
    );
    return otherImage;
  }

  public static getImageBySelectorOrFirst(
    visualModel: Nil<VehicleVisualModelDto>,
    selector: Nil<VisualModelImageSelector>,
    points?: Nil<VisualModelPoint[]>,
  ): Nil<VehicleVisualModelImageDto> {
    return (
      this.getImageBySelector(visualModel, selector, points) || this.getFirstImage(visualModel)
    );
  }

  public static getVisualModelPointsForImage<TPoint extends VisualModelPoint>(
    image: Nil<VehicleVisualModelImageDto>,
    visualModelPoints: TPoint[],
  ): TPoint[] {
    return image
      ? visualModelPoints.filter(
          (point) => point.meta.area === image.area && point.meta.projection == image.projection,
        )
      : [];
  }

  /** Returns insights on selecting one visual model from a list to display damages.
   * Pick visual model based on the damages for this cases:
   * 1. None. None of the damages have visual model - use default visual model.
   * 2. All. All damages have the same visual model - use uniform visual model.
   * 3. Mixed. Some damages have different visual models - use the most common.
   */
  public static pickForDamagesDisplay(visualModelIds: string[]) {
    const visualModelIdsAll = visualModelIds.filter(Boolean);
    const visualModelIdsUnique = uniq(visualModelIdsAll);
    const isNone = TypeHelper.isEmptyArray(visualModelIdsAll);
    const isUniform = ArrayHelper.isAllUniform(visualModelIdsAll);
    const visualModelIdFirstMostFrequent = ArrayHelper.findFirstMostFrequent(visualModelIdsAll);
    const visualModelIdPrimary = visualModelIdFirstMostFrequent;
    return {
      isNone,
      isUniform,
      isMixed: !isUniform,
      isUseDefault: isNone,
      visualModelIdsAll,
      visualModelIdsUnique,
      visualModelIdFirstMostFrequent: visualModelIdFirstMostFrequent,
      visualModelIdPrimary,
      visualModelIdsForeign: visualModelIdsUnique.filter((id) => id !== visualModelIdPrimary),
    };
  }

  // #region SVG

  public static readonly svgPointColorMap = {
    default: "gray",
    damage: "red",
    damageActual: "red",
    damageRepaired: "green",
    damageInactive: "gray",
    damageOld: "orange",
  };

  public static readonly defaultSvgPointStyle: SvgPointStyle = {
    color: this.svgPointColorMap.default,
    // circleRadius: 3.5,
    circleRadiusPct: 1.5,
  };

  public static readonly defaultSvgPointStyleForDamage: SvgPointStyle = {
    color: this.svgPointColorMap.damage,
    // circleRadius: 3.5,
    circleRadiusPct: 1.5,
  };

  public static getSvgPointStyleForVehicleDamage(
    source: VehicleDamageDto | VehicleDamageDataDto,
  ): SvgPointStyle {
    return {
      ...this.defaultSvgPointStyleForDamage,
      color:
        source.state === VehicleDamageState.Actual
          ? this.svgPointColorMap.damageActual
          : source.state === VehicleDamageState.Repaired
            ? this.svgPointColorMap.damageRepaired
            : source.state === VehicleDamageState.Inactive
              ? this.svgPointColorMap.damageInactive
              : this.defaultSvgPointStyleForDamage.color,
    };
  }

  public static getSvgPointStyleForOldVehicleDamage(
    source: VehicleDamageDto | VehicleDamageDataDto,
  ): SvgPointStyle {
    return {
      ...this.defaultSvgPointStyleForDamage,
      color:
        source.state === VehicleDamageState.Actual
          ? this.svgPointColorMap.damageOld
          : source.state === VehicleDamageState.Repaired
            ? this.svgPointColorMap.damageRepaired
            : source.state === VehicleDamageState.Inactive
              ? this.svgPointColorMap.damageInactive
              : this.defaultSvgPointStyleForDamage.color,
    };
  }

  public static async fetchSvgContent(svgUrl: string): Promise<string> {
    try {
      const response = await fetch(svgUrl, {
        // use standard HTTP caching logic,
        // but as Azure Blob SAS URL might include new signature query param every time the browser won't cache the image
        cache: "default",
      });
      return await response.text();
    } catch (err) {
      console.error(`Error while fetching SVG by URL.`, { svgUrl }, err);
      throw err;
    }
  }

  public static getMetadataFromSvgPathElement(
    pathEl: Nil<Element>,
  ): VehicleVisualModelSvgPathMetadataDto | null {
    if (!pathEl || !SvgHelper.isPathElement(pathEl)) {
      return null;
    }
    const descEl = pathEl.querySelector("desc");
    const metadataJson = descEl?.innerHTML;
    if (!metadataJson) {
      return null;
    }
    let metadata: VehicleVisualModelSvgPathMetadataDto = {
      vehicleProjection: undefined,
      vehicleArea: undefined,
    };
    try {
      metadata = JSON.parse(metadataJson);
    } catch (err) {
      console.error(`Error while parsing SVG <path/> metadata.`, err);
      return null;
    }
    return metadata;
  }

  // #endregion SVG
}

// short alias
export const VisualModelHelper = VehicleVisualModelHelper;
